import React, { useContext, useEffect, useRef, useState } from 'react';
import B2BTableInMemory from '../../b2b-common/B2BTableInMemory';
import { LoginContext } from '../../../App';
import { fetchMeasurementUnitsData, getConversionsCSV, getMeasurementUnitsCSV, getMeasurementUnitsCategoriesCSV, importingMeasurementUnitsCSVFile } from '../../../services/b2b-indicators-apiService';
import { Category, Download, FileUpload, Straighten, Transform } from '@mui/icons-material';
import ConversionModal from '../modal/ConversionModal';
import MeasurementUnitModal from '../modal/MeasurementUnitModal';

import './MeasurementUnitsTable.css';
import B2BDialog from '../../b2b-common/B2BDialog';
import { callAPIWhileSettingState, get_YYMMDDHHMM } from '../../b2b-common/helpers';

const MeasurementUnitsTable = ({ onSelectedMeasurementUnit }) => {

    const { loginData } = useContext(LoginContext);
    const [measurementUnits, setMeasurementUnits] = useState([]);
    const [error, setError] = useState(null);

    const [openConversionModal, setOpenConversionModal] = React.useState(false);
    const [openUnitModal, setOpenUnitModal] = React.useState(false);

    const getMeasurementUnits = async () => {
        try {
            const { accessToken } = loginData;
            const data = await fetchMeasurementUnitsData(accessToken);
            setMeasurementUnits(data);
        } catch (error) {
            setError(error.message);
        }
    }

    useEffect(() => {
        if (measurementUnits.length === 0) {
            getMeasurementUnits();
        }
    }, []);

    const headers = [
        { label: 'Unit', align: 'center' },
        { label: 'Unit name', align: 'left' },
        { label: 'Used #', align: 'center' },
        { label: 'Conv. to', align: 'center' },
        { label: 'Conv. from', align: 'center' },
    ];

    const rows = measurementUnits ? measurementUnits.map((unit) => ({
        id: unit.quantitativeMeasurementUnitId,
        cells: [
            { content: `${unit.quantitativeMeasurementUnit}`, align: 'center' },
            { content: `${unit.quantitativeMeasurementUnitName}`, align: 'left' },
            { content: `${unit.usedCount}`, align: 'center' },
            { content: `${unit.convertToUnitsCount}`, align: 'center' },
            { content: `${unit.convertFromUnitsCount}`, align: 'center' },
        ]
    })) : [];

    const [selectedUnit, setSelectedUnit] = useState(null);

    const handleSelectMeasurementUnit = (unitId) => {
        const selectedUnit = measurementUnits.find((unit) => unit.quantitativeMeasurementUnitId === unitId.id);
        setSelectedUnit(selectedUnit);
        setOpenUnitModal(true);
        onSelectedMeasurementUnit(selectedUnit);
    };

    const handleSaveUpdateConversion = () => {
        setOpenConversionModal(false);
        getMeasurementUnits();
        // console.log('Save/Update conversion');
    }

    const handleSaveUpdateUnit = () => {
        setOpenUnitModal(false);
        getMeasurementUnits();
        // console.log('Save/Update unit');
    }

    const [downloadingCSV, setDownloadingCSV] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const handleDownloadingMeasurementUnitsCSV = async () => {
        const fileName = 'b2b-measurement-units-' + get_YYMMDDHHMM() + '.csv';
        const paramsArray = [
            loginData.accessToken,
            fileName
        ];
        try {
            await callAPIWhileSettingState(getMeasurementUnitsCSV, paramsArray,
                setDownloadingCSV, null);
        } catch (error) {
            setDialogOpen(true);
            setDialogError('Failed to download csv: ' + error.message);
        } finally {
            setDownloadingCSV(false);
        }
    }

    const handleDownloadingConversionsCSV = async () => {
        const fileName = 'b2b-measurement-units-conversions-' + get_YYMMDDHHMM() + '.csv';
        const paramsArray = [
            loginData.accessToken,
            fileName
        ];
        try {
            await callAPIWhileSettingState(getConversionsCSV, paramsArray,
                setDownloadingCSV, null);
        } catch (error) {
            setDialogOpen(true);
            setDialogError('Failed to download csv: ' + error.message);
        } finally {
            setDownloadingCSV(false);
        }
    }

    const handleDownloadingMeasurementUnitsCategoriesCSV = async () => {
        const fileName = 'b2b-measurement-units-categories-' + get_YYMMDDHHMM() + '.csv';
        const paramsArray = [
            loginData.accessToken,
            fileName
        ];
        try {
            await callAPIWhileSettingState(getMeasurementUnitsCategoriesCSV, paramsArray,
                setDownloadingCSV, null);
        } catch (error) {
            setDialogOpen(true);
            setDialogError('Failed to download csv: ' + error.message);
        } finally {
            setDownloadingCSV(false);
        }
    }

    // Importing CSV

    const csvFileInputRef = useRef(null);
    const [processingCSVFile, setProcessingCSVFile] = useState(false);

    const handleUploadingCSV = () => {
        csvFileInputRef.current.click();
    }

    const handleCSVFileUpload = async (e) => {
        const paramsArray = [
            e.target.files[0],
            ';',
            loginData.accessToken
        ];
        try {
            await callAPIWhileSettingState(importingMeasurementUnitsCSVFile, paramsArray,
                setProcessingCSVFile, onCSVImported);
            getMeasurementUnits();
        } catch (error) {
            setDialogOpen(true);
            setDialogError('Failed to import csv: ' + error.message);
        } finally {
            e.target.value = null;
        }
    }

    const onCSVImported = () => {
        setDialogOpen(true);
        setDialogError(null);
    }


    const buttons = [
        {
            label: 'New Unit',
            disabledText: 'New Unit',
            onClick: () => { setOpenUnitModal(true) },
            color: 'secondary',
            variant: 'contained',
            key: '1',
            icon: <Straighten />,
        },
        {
            label: 'New Conversion',
            disabledText: 'New Conversion',
            onClick: () => { setOpenConversionModal(true) },
            color: 'secondary',
            variant: 'contained',
            key: '2',
            icon: <Transform />,
        },
        {
            label: 'Get CSV',
            disabledText: 'Get CSV',
            onClick: () => { return null },
            color: 'secondary',
            variant: 'contained',
            key: '3',
            icon: <Download />,
            subMenu: [
                {
                    label: 'Measurement Units',
                    disabledText: 'Measurement Units',
                    onClick: () => { handleDownloadingMeasurementUnitsCSV() },
                    key: '3.1',
                    icon: <Straighten />,
                },
                {
                    label: 'Conversions',
                    disabledText: 'Conversions',
                    onClick: () => { handleDownloadingConversionsCSV() },
                    key: '3.2',
                    icon: <Transform />,
                },
                {
                    label: 'Categories',
                    disabledText: 'Categories',
                    onClick: () => { handleDownloadingMeasurementUnitsCategoriesCSV() },
                    key: '3.3',
                    icon: <Category />,
                },
            ],
        },
        {
            label: 'Import CSV',
            disabledText: 'Import CSV',
            onClick: () => { return null },
            color: 'secondary',
            variant: 'contained',
            key: '4',
            icon: <FileUpload
            />,
            subMenu: [
                {
                    label: 'Measurement Units',
                    disabledText: 'Measurement Units',
                    onClick: () => { handleUploadingCSV() },
                    key: '4.1',
                    icon: <Straighten />,
                },
            ],
        },
    ]

    return (
        <>
            <B2BTableInMemory
                headers={headers}
                rows={rows}
                className={"measurement-units-list-table"}
                numRows={15}
                stickyHeader={false}
                onRowClick={handleSelectMeasurementUnit}
                buttons={buttons}
            />
            <MeasurementUnitModal
                open={openUnitModal}
                toChangeUnitId={selectedUnit ? selectedUnit.quantitativeMeasurementUnitId : null}
                onClose={() => {
                    setOpenUnitModal(false);
                    setSelectedUnit(null);
                }}
                onUpdate={handleSaveUpdateUnit}
            />
            <ConversionModal
                open={openConversionModal}
                onClose={() => setOpenConversionModal(false)}
                onUpdate={handleSaveUpdateConversion}
            />
            <B2BDialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setDialogError(null);
                }}
                title={dialogError ? 'Error downloading CSV' : 'CSV Downloaded'}
            >
                <p>{dialogError || "The csv file has been downloaded successfully."}</p>
            </B2BDialog>
            <input
                ref={csvFileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleCSVFileUpload}
            />
        </>
    );
};

export default MeasurementUnitsTable;